import { Link, useParams } from "@tanstack/solid-router";
import { For, Show, createEffect, createMemo, createSignal } from "solid-js";

import { AppShell } from "~/components/AppShell";
import { CustomFieldsRenderer } from "~/components/CustomFieldsRenderer";
import { DealLineItemsCard } from "~/components/DealLineItemsCard";
import { TagBadges, TagsEditor } from "~/components/TagsEditor";
import { UserPicker } from "~/components/UserPicker";
import { recordView } from "~/lib/recentViews";
import { BriefModal } from "~/components/BriefModal";
import { DealDraftEmailDialog } from "~/components/DealDraftEmailDialog";
import { EntityTimeline } from "~/components/EntityTimeline";
import {
  useAbandonDeal,
  useDeal,
  useGenerateDealBrief,
  useMarkDealLost,
  useMarkDealWon,
  useMoveDealToStage,
  usePipelines,
  useStages,
  useUpdateDeal,
  useWorkspaceUser,
} from "~/lib/hooks";
import type { BriefResponse, DealStatus, StageRead } from "~/lib/types";

const STATUS_BADGE: Record<DealStatus, string> = {
  open: "badge-outline",
  won: "badge-success",
  lost: "badge-error",
  abandoned: "badge-ghost",
};

function formatMoney(amount: number | null, currency: string | null): string {
  if (amount === null) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency ?? "USD",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount} ${currency ?? ""}`.trim();
  }
}

export function DealDetailPage() {
  const params = useParams({ strict: false }) as () => {
    wsKey: string;
    dealId: string;
  };

  const deal = useDeal(
    () => params().wsKey,
    () => params().dealId,
  );
  const pipelines = usePipelines(() => params().wsKey);
  const stages = useStages(
    () => params().wsKey,
    () => deal.data?.pipeline_id,
  );

  const pipelineName = createMemo(
    () => pipelines.data?.find((p) => p.id === deal.data?.pipeline_id)?.name ?? "—",
  );

  const updateMutation = useUpdateDeal(() => params().wsKey);
  const moveMutation = useMoveDealToStage(() => params().wsKey);
  const wonMutation = useMarkDealWon(() => params().wsKey);
  const lostMutation = useMarkDealLost(() => params().wsKey);
  const abandonMutation = useAbandonDeal(() => params().wsKey);

  const [editing, setEditing] = createSignal(false);
  const briefMutation = useGenerateDealBrief(() => params().wsKey);
  const [showBrief, setShowBrief] = createSignal(false);
  const [showDraftEmail, setShowDraftEmail] = createSignal(false);
  const [briefError, setBriefError] = createSignal<string | null>(null);
  const [briefResult, setBriefResult] = createSignal<BriefResponse | null>(null);
  const runBrief = async () => {
    setShowBrief(true);
    setBriefError(null);
    setBriefResult(null);
    try {
      const r = await briefMutation.mutateAsync({ dealId: params().dealId });
      setBriefResult(r);
    } catch (err) {
      setBriefError((err as Error).message);
    }
  };
  const [formError, setFormError] = createSignal<string | null>(null);
  const [showLostDialog, setShowLostDialog] = createSignal(false);
  const [customDraft, setCustomDraft] = createSignal<Record<string, unknown>>({});
  const [tagsDraft, setTagsDraft] = createSignal<string[]>([]);
  const [ownerDraft, setOwnerDraft] = createSignal<string | null>(null);
  const [ownerNameDraft, setOwnerNameDraft] = createSignal<string | null>(null);

  const owner = useWorkspaceUser(
    () => params().wsKey,
    () => deal.data?.owner_user_id ?? null,
  );

  createEffect(() => {
    if (editing() && deal.data) {
      setCustomDraft({ ...(deal.data.custom_fields ?? {}) });
      setTagsDraft([...(deal.data.tags ?? [])]);
      setOwnerDraft(deal.data.owner_user_id ?? null);
      setOwnerNameDraft(owner.data ? (owner.data.display_name ?? owner.data.email) : null);
    }
  });

  createEffect(() => {
    const d = deal.data;
    if (!d) return;
    recordView(params().wsKey, {
      entity_type: "deal",
      id: d.id,
      name: d.name,
      subtitle: d.status,
    });
  });

  const handleStageClick = async (stage: StageRead) => {
    if (!deal.data) return;
    if (stage.id === deal.data.stage_id) return;
    setFormError(null);
    try {
      await moveMutation.mutateAsync({
        dealId: deal.data.id,
        stage_id: stage.id,
        version: deal.data.version,
      });
    } catch (err) {
      setFormError((err as Error).message);
    }
  };

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault();
    if (!deal.data) return;
    setFormError(null);
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    const amountStr = String(data.get("amount") ?? "").trim();
    try {
      const body: Record<string, unknown> = {
        name: String(data.get("name") ?? deal.data.name),
        description: (String(data.get("description") ?? "") || null) as string | null,
        amount: amountStr ? Number(amountStr) : null,
        currency: (String(data.get("currency") ?? "") || null) as string | null,
        expected_close_date: (String(data.get("expected_close_date") ?? "") || null) as
          | string
          | null,
        next_step: (String(data.get("next_step") ?? "") || null) as string | null,
        next_step_due: (String(data.get("next_step_due") ?? "") || null) as string | null,
        source: (String(data.get("source") ?? "") || null) as string | null,
        custom_fields: customDraft(),
        tags: tagsDraft(),
      };
      if (ownerDraft() !== (deal.data.owner_user_id ?? null)) {
        body.owner_user_id = ownerDraft();
      }
      await updateMutation.mutateAsync({
        dealId: deal.data.id,
        version: deal.data.version,
        body,
      });
      setEditing(false);
    } catch (err) {
      const e = err as Error & { code?: string };
      if (e.code === "OptimisticLockError") {
        setFormError(
          "This deal was changed by someone else. Reload to see the latest, then re-apply your edits.",
        );
      } else {
        setFormError(e.message);
      }
    }
  };

  const handleMarkLost = async (e: SubmitEvent) => {
    e.preventDefault();
    if (!deal.data) return;
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    const reason = String(data.get("lost_reason") ?? "").trim();
    if (!reason) return;
    try {
      await lostMutation.mutateAsync({
        dealId: deal.data.id,
        version: deal.data.version,
        lost_reason: reason,
        lost_reason_detail: String(data.get("lost_reason_detail") ?? "").trim() || undefined,
      });
      setShowLostDialog(false);
    } catch (err) {
      setFormError((err as Error).message);
    }
  };

  return (
    <AppShell
      wsKey={params().wsKey}
      pageTitle="Deal"
      rightSlot={
        <Link
          to="/workspaces/$wsKey/deals"
          params={{ wsKey: params().wsKey }}
          class="btn btn-ghost btn-sm"
        >
          ← Back to list
        </Link>
      }
    >
      <Show when={deal.isLoading}>
        <div role="status" aria-live="polite" class="text-center py-8 opacity-60">
          Loading…
        </div>
      </Show>
      <Show when={deal.isError}>
        <div role="alert" class="alert alert-error">
          Failed to load deal.
        </div>
      </Show>

      <Show when={deal.data}>
        {(data) => (
          <div class="flex flex-col gap-4">
            {/* Header card */}
            <div class="card bg-base-100 shadow-sm">
              <div class="card-body">
                <div class="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <h2 class="card-title">{data().name}</h2>
                    <div class="flex gap-2 mt-2 flex-wrap items-center">
                      <span class={`badge ${STATUS_BADGE[data().status]}`}>{data().status}</span>
                      <span class="badge badge-ghost">{pipelineName()}</span>
                      <span class="text-sm font-medium">
                        {formatMoney(data().amount, data().currency)}
                      </span>
                      <Show when={data().probability_pct !== null}>
                        <span class="text-xs opacity-60">
                          · {data().probability_pct}% probability
                        </span>
                      </Show>
                    </div>
                  </div>
                  <div class="flex gap-2 flex-wrap">
                    <Show when={data().status === "open" && !editing()}>
                      <button
                        class="btn btn-success btn-sm"
                        onClick={() =>
                          wonMutation.mutate({
                            dealId: data().id,
                            version: data().version,
                          })
                        }
                        disabled={wonMutation.isPending}
                      >
                        Mark won
                      </button>
                      <button
                        class="btn btn-error btn-sm btn-outline"
                        onClick={() => setShowLostDialog(true)}
                      >
                        Mark lost
                      </button>
                      <button
                        class="btn btn-ghost btn-sm"
                        onClick={() =>
                          abandonMutation.mutate({
                            dealId: data().id,
                            version: data().version,
                          })
                        }
                        disabled={abandonMutation.isPending}
                      >
                        Abandon
                      </button>
                    </Show>
                    <Show when={!editing()}>
                      <button
                        type="button"
                        class="btn btn-ghost btn-sm"
                        onClick={runBrief}
                        disabled={briefMutation.isPending}
                        aria-label="Generate an AI brief about this deal"
                      >
                        {briefMutation.isPending ? "Generating…" : "✨ Generate brief"}
                      </button>
                      <button
                        type="button"
                        class="btn btn-ghost btn-sm"
                        onClick={() => setShowDraftEmail(true)}
                        aria-label="Draft an email on behalf of this deal"
                      >
                        ✉️ Draft email
                      </button>
                      <button class="btn btn-primary btn-sm" onClick={() => setEditing(true)}>
                        Edit
                      </button>
                    </Show>
                  </div>
                </div>

                {/* Stage strip */}
                <Show when={stages.data?.length}>
                  <div class="flex flex-wrap gap-1 mt-4" role="group" aria-label="Pipeline stages">
                    <For each={stages.data ?? []}>
                      {(stage) => (
                        <button
                          type="button"
                          class={`btn btn-xs ${
                            stage.id === data().stage_id
                              ? stage.stage_type === "won"
                                ? "btn-success"
                                : stage.stage_type === "lost"
                                  ? "btn-error"
                                  : "btn-primary"
                              : "btn-outline"
                          }`}
                          disabled={stage.id === data().stage_id || moveMutation.isPending}
                          onClick={() => void handleStageClick(stage)}
                          aria-current={stage.id === data().stage_id ? "step" : undefined}
                        >
                          {stage.name}
                        </button>
                      )}
                    </For>
                  </div>
                </Show>

                <Show when={formError()}>
                  <div role="alert" class="alert alert-error text-sm mt-3">
                    {formError()}
                  </div>
                </Show>
              </div>
            </div>

            {/* Detail / edit */}
            <Show when={!editing()}>
              <div class="card bg-base-100 shadow-sm">
                <div class="card-body">
                  <dl class="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                    <DetailRow
                      label="Expected close"
                      value={
                        data().expected_close_date
                          ? new Date(data().expected_close_date!).toLocaleDateString()
                          : null
                      }
                    />
                    <DetailRow
                      label="Actual close"
                      value={
                        data().actual_close_date
                          ? new Date(data().actual_close_date!).toLocaleDateString()
                          : null
                      }
                    />
                    <DetailRow label="Next step" value={data().next_step} />
                    <DetailRow
                      label="Next step due"
                      value={
                        data().next_step_due
                          ? new Date(data().next_step_due!).toLocaleDateString()
                          : null
                      }
                    />
                    <DetailRow label="Source" value={data().source} />
                    <DetailRow
                      label="Account"
                      value={data().account_id}
                      linkTo={
                        data().account_id
                          ? {
                              to: "/workspaces/$wsKey/accounts/$accId",
                              params: {
                                wsKey: params().wsKey,
                                accId: data().account_id!,
                              },
                            }
                          : null
                      }
                    />
                    <DetailRow
                      label="Primary contact"
                      value={data().primary_contact_id}
                      linkTo={
                        data().primary_contact_id
                          ? {
                              to: "/workspaces/$wsKey/contacts/$conId",
                              params: {
                                wsKey: params().wsKey,
                                conId: data().primary_contact_id!,
                              },
                            }
                          : null
                      }
                    />
                    <DetailRow
                      label="Owner"
                      value={
                        owner.data
                          ? (owner.data.display_name ?? owner.data.email)
                          : data().owner_user_id
                            ? "—"
                            : null
                      }
                    />
                    <Show when={data().status === "lost" && data().lost_reason}>
                      <DetailRow label="Lost reason" value={data().lost_reason} />
                    </Show>
                    <DetailRow label="Version" value={String(data().version)} />
                  </dl>
                  <Show when={data().description}>
                    <div class="mt-4">
                      <div class="text-xs uppercase opacity-50 mb-1">Description</div>
                      <p class="whitespace-pre-wrap">{data().description}</p>
                    </div>
                  </Show>
                  <Show when={(data().tags?.length ?? 0) > 0}>
                    <div class="mt-4">
                      <div class="text-xs uppercase opacity-50 mb-1">Tags</div>
                      <TagBadges tags={() => data().tags} />
                    </div>
                  </Show>
                  <div class="mt-4">
                    <CustomFieldsRenderer
                      wsKey={() => params().wsKey}
                      entityType="deal"
                      values={() => data().custom_fields}
                      onChange={() => {
                        /* read-only in view mode */
                      }}
                      variant="view"
                    />
                  </div>
                </div>
              </div>
            </Show>

            <DealLineItemsCard
              wsKey={() => params().wsKey}
              dealId={() => data().id}
              dealCurrency={() => data().currency}
              editable={() => data().archived_at === null}
            />

            <Show when={editing()}>
              <form
                onSubmit={handleSubmit}
                class="card bg-base-100 shadow-sm"
                aria-label="Edit deal"
              >
                <div class="card-body grid grid-cols-1 md:grid-cols-2 gap-3">
                  <LabeledInput name="name" label="Name" value={data().name} required />
                  <div />
                  <LabeledInput
                    name="amount"
                    label="Amount"
                    type="number"
                    value={data().amount?.toString() ?? ""}
                  />
                  <LabeledInput name="currency" label="Currency" value={data().currency ?? "EUR"} />
                  <LabeledInput
                    name="expected_close_date"
                    label="Expected close date"
                    type="date"
                    value={data().expected_close_date ?? ""}
                  />
                  <LabeledInput
                    name="next_step_due"
                    label="Next step due"
                    type="date"
                    value={data().next_step_due ?? ""}
                  />
                  <LabeledInput name="next_step" label="Next step" value={data().next_step ?? ""} />
                  <LabeledInput name="source" label="Source" value={data().source ?? ""} />
                  <div class="md:col-span-2">
                    <label for="deal-description" class="label">
                      <span class="label-text">Description</span>
                    </label>
                    <textarea
                      id="deal-description"
                      name="description"
                      class="textarea textarea-bordered w-full"
                      rows="3"
                    >
                      {data().description ?? ""}
                    </textarea>
                  </div>
                  <div class="md:col-span-2">
                    <label class="label">
                      <span class="label-text">Tags</span>
                    </label>
                    <TagsEditor
                      wsKey={() => params().wsKey}
                      entityType="deal"
                      value={tagsDraft}
                      onChange={setTagsDraft}
                    />
                  </div>
                  <div>
                    <label class="label">
                      <span class="label-text">Owner</span>
                    </label>
                    <UserPicker
                      wsKey={() => params().wsKey}
                      value={ownerDraft}
                      selectedName={ownerNameDraft}
                      onChange={(id, name) => {
                        setOwnerDraft(id);
                        setOwnerNameDraft(name);
                      }}
                    />
                  </div>
                  <div class="md:col-span-2">
                    <CustomFieldsRenderer
                      wsKey={() => params().wsKey}
                      entityType="deal"
                      values={() => customDraft()}
                      onChange={(key, value) =>
                        setCustomDraft((prev) => ({ ...prev, [key]: value }))
                      }
                    />
                  </div>
                  <Show when={formError()}>
                    <div role="alert" class="md:col-span-2 alert alert-error text-sm">
                      {formError()}
                    </div>
                  </Show>
                  <div class="md:col-span-2 flex gap-2 justify-end">
                    <button type="button" class="btn btn-ghost" onClick={() => setEditing(false)}>
                      Cancel
                    </button>
                    <button
                      type="submit"
                      class="btn btn-primary"
                      disabled={updateMutation.isPending}
                    >
                      {updateMutation.isPending ? "Saving…" : "Save"}
                    </button>
                  </div>
                </div>
              </form>
            </Show>

            <EntityTimeline
              wsId={() => params().wsKey}
              entityType="deal"
              entityId={() => data().id}
            />
          </div>
        )}
      </Show>

      <Show when={showLostDialog()}>
        <dialog open class="modal modal-open" aria-modal="true" aria-labelledby="lost-dialog-title">
          <div class="modal-box">
            <h3 id="lost-dialog-title" class="font-bold text-lg mb-4">
              Mark deal as lost
            </h3>
            <form onSubmit={handleMarkLost} class="flex flex-col gap-3">
              <label for="lost-reason-field" class="label">
                <span class="label-text">Reason</span>
              </label>
              <input
                id="lost-reason-field"
                name="lost_reason"
                class="input input-bordered"
                placeholder="e.g. price, timing, no-decision"
                required
              />
              <label for="lost-detail-field" class="label">
                <span class="label-text">Detail (optional)</span>
              </label>
              <textarea
                id="lost-detail-field"
                name="lost_reason_detail"
                class="textarea textarea-bordered"
                rows="3"
              />
              <div class="modal-action">
                <button
                  type="button"
                  class="btn btn-ghost"
                  onClick={() => setShowLostDialog(false)}
                >
                  Cancel
                </button>
                <button type="submit" class="btn btn-error" disabled={lostMutation.isPending}>
                  {lostMutation.isPending ? "Saving…" : "Mark lost"}
                </button>
              </div>
            </form>
          </div>
        </dialog>
      </Show>

      <Show when={showBrief()}>
        <BriefModal
          subjectLabel={deal.data?.name ?? "this deal"}
          pending={briefMutation.isPending}
          error={briefError()}
          result={briefResult()}
          onClose={() => setShowBrief(false)}
          onRegenerate={runBrief}
        />
      </Show>

      <Show when={showDraftEmail()}>
        <DealDraftEmailDialog
          wsKey={() => params().wsKey}
          dealId={() => params().dealId}
          dealName={() => deal.data?.name ?? "this deal"}
          primaryContactId={() => deal.data?.primary_contact_id ?? null}
          accountId={() => deal.data?.account_id ?? null}
          onClose={() => setShowDraftEmail(false)}
        />
      </Show>
    </AppShell>
  );
}

function DetailRow(props: {
  label: string;
  value: string | null;
  linkTo?: { to: string; params: Record<string, string> } | null;
}) {
  return (
    <div>
      <dt class="text-xs uppercase opacity-50">{props.label}</dt>
      <dd class="font-medium">
        <Show when={props.value} fallback={<>—</>}>
          <Show when={props.linkTo} fallback={<span>{props.value}</span>}>
            {(linkProps) => (
              <Link to={linkProps().to} params={linkProps().params} class="link link-primary">
                {props.value}
              </Link>
            )}
          </Show>
        </Show>
      </dd>
    </div>
  );
}

function LabeledInput(props: {
  name: string;
  label: string;
  value: string;
  type?: string;
  required?: boolean;
}) {
  const id = `deal-field-${props.name}`;
  return (
    <div>
      <label for={id} class="label">
        <span class="label-text">{props.label}</span>
      </label>
      <input
        id={id}
        name={props.name}
        class="input input-bordered w-full"
        value={props.value}
        type={props.type ?? "text"}
        required={props.required}
      />
    </div>
  );
}
